import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import _ from 'lodash';
import { RowError } from '../../../components/import/VerifyImportModal';
import { ITableConfigurableModules } from '../../../types/Itable';
import CustomAxios from '../../../utility/customAxios';
import { queryKeys } from '../queryKeys';
import { queryClient } from '../queryClient';

type ImportPayload = {
  module: ITableConfigurableModules;
  fileBase64: string;
};

type ImportErrorResponse = {
  error?: unknown;
};

const MODULE_TO_QUERY_KEY_MAP: Partial<Record<ITableConfigurableModules, string>> = {
  accounts: queryKeys.allAccounts,
  contacts: queryKeys.allContacts,
  employees: queryKeys.allEmployees,
};

const parseErrors = (raw: any): RowError[] => {
  if (_.isString(raw)) {
    return [{ row: 0, issues: [raw] }];
  }

  if (_.isPlainObject(raw)) {
    return _.map(raw, (value, key) => ({
      row: Number(_.get(key.match(/\d+/), 0, 0)),
      issues: _.castArray(value),
    }));
  }

  return [];
};

const postImportCsv = async ({ module, fileBase64 }: ImportPayload) => {
  const response = await CustomAxios.post(`/${module}/import`, {
    file: fileBase64,
  });

  return response.data;
};

type UseImportCsvProps = {
  onSuccess: () => void;
  onRowErrors: (errors: RowError[]) => void;
};

export function useImportCsv({ onSuccess, onRowErrors }: UseImportCsvProps) {
  return useMutation({
    mutationFn: postImportCsv,
    onSuccess: (_data, variables) => {
      const listQueryKey = MODULE_TO_QUERY_KEY_MAP[variables.module];

      if (listQueryKey) {
        queryClient.invalidateQueries({
          queryKey: [listQueryKey],
        });
      }
      onSuccess();
    },
    onError: (err: AxiosError<ImportErrorResponse>) => {
      const data = err.response?.data;
      const parsed = parseErrors(data?.error);

      if (!_.isEmpty(parsed)) {
        onRowErrors(parsed);
      } else {
        console.error('CSV import failed', err);
      }
    },
  });
}
